import React, { FC, ReactElement } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { FormNodeVolunteerOptionModel } from '../../../../../shared/volunteer/form';
import { useTranslation } from '../../../../translations';
import { Fetcher, useFetcher } from '../../../../utils';
import {
  AbstractElementEditor,
  UpdateFormProps,
  UpdateFormPropsNoTrans,
} from './AbstractElementEditor';

export class VolunteerOptionEditor extends AbstractElementEditor<FormNodeVolunteerOptionModel> {
  public getDescription(): string {
    return 'Entry for an option for a volunteer.';
  }

  public getName(): string {
    return 'Volunteer Option';
  }

  public getIcon(): string {
    return 'settings';
  }

  public override getDefaultValues(): Partial<Omit<FormNodeVolunteerOptionModel, 'id' | 'type'>> {
    return {
      optionId: -1,
    };
  }

  public override getUpdateForm({
    node,
    update,
  }: UpdateFormProps<FormNodeVolunteerOptionModel>): ReactElement {
    return (
      <VolunteerOptionUpdateForm
        node={node}
        update={(n) => {
          update(n);
        }}
      />
    );
  }
}

// eslint-disable-next-line @typescript-eslint/no-deprecated
const VolunteerOptionUpdateForm: FC<UpdateFormPropsNoTrans<FormNodeVolunteerOptionModel>> = ({
  node,
  update,
}) => {
  const { ts } = useTranslation();

  const options = useFetcher(async () => {
    return await api.getOptions('volunteer');
  }, []);

  if (!options.data) {
    return <Fetcher result={options} />;
  }

  return (
    <>
      <Label for="volunteerOptionId">{ts('option')}</Label>
      <Input
        defaultValue={node.optionId}
        id="volunteerOptionId"
        onChange={(e) => {
          const optionId = Number.parseInt(e.target.value, 10);
          update({ ...node, optionId });
        }}
        type="select"
      >
        <option disabled value={-1} />
        {options.data.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </Input>
      <Label for="volunteerOptionDisplay">{ts('option_editor_select.display_name_optional')}</Label>
      <Input
        defaultValue={node.displayName ?? ''}
        id="volunteerOptionDisplay"
        onChange={(e) => {
          update({ ...node, displayName: e.target.value });
        }}
        type="text"
      />
      <FormGroup check className="py-2">
        <Input
          checked={node.required ?? false}
          id="volunteerOptionRequired"
          onChange={(e) => {
            update({ ...node, required: e.target.checked });
          }}
          type="checkbox"
        />
        <Label check for="volunteerOptionRequired">
          {ts('required_for_submission')}
        </Label>
      </FormGroup>
      <FormGroup check className="py-2">
        <Input
          checked={node.hidden ?? false}
          id="volunteerOptionHidden"
          onChange={(e) => {
            update({ ...node, hidden: e.target.checked });
          }}
          type="checkbox"
        />
        <Label check for="volunteerOptionHidden">
          {ts('visible_to_admins_only')}
        </Label>
      </FormGroup>
      <FormGroup check className="py-2">
        <Input
          checked={node.applicationOnly ?? false}
          id="applicationOnly"
          onChange={(e) => {
            update({ ...node, applicationOnly: e.target.checked });
          }}
          type="checkbox"
        />
        <Label check for="applicationOnly">
          {ts('disable_after_approval')}
        </Label>
      </FormGroup>
    </>
  );
};
